<template>
  <v-card id="bid-product-form">
    <v-card-title class="primary white--text">
      <span>{{ isEditing ? $t('editProduct') : $t('addProducts') }}</span>
      <v-spacer/>
      <BaseDialogActions
      hideRefresh
      hideFullscreen
      customCancel
      elementId="bid-product-dialog-actions"
      @close="$emit('close')"/>
    </v-card-title>
    <v-card-text class="pt-4">
      <div v-if="!isEditing && usesCostTypes" class="caption text--secondary pb-3">{{ $t(mixedCosts ? 'bidProductHintMixed' : 'bidProductHint') }}</div>
      <FormWrapper
      formRef="bidProductForm"
      :buttonText="isEditing ? $t('save') : $t('addProduct')"
      :disabled="!complete || saving"
      @submit="save">
        <v-card outlined class="mb-4" v-if="!isEditing && destinationProducts.length > 0">
          <v-card-text class="pt-2 px-3 pb-2">
            <v-row dense no-gutters class="caption grey--text text--darken-1">
              <v-col cols="6">{{ $t('product') }}</v-col>
              <v-col cols="3" class="text-right">{{ $t('volume') }}</v-col>
              <v-col cols="3" class="text-right">{{ $t('deliveredRate') }}</v-col>
            </v-row>
            <v-row
            v-for="existing in destinationProducts"
            :key="existing.bidProductId"
            dense
            no-gutters>
              <v-col cols="6">{{ existing.productName }}</v-col>
              <v-col cols="3" class="text-right text-no-wrap">{{ numberWithCommas(existing.harvestVolume, 3) }} t</v-col>
              <v-col cols="3" class="text-right">{{ formatMoney(existing.deliveredRate) }}</v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-row dense :key="entryKey">
          <v-col :cols="rateIsDerived ? 8 : 6">
            <v-text-field
            v-if="isEditing"
            :value="productName"
            :label="$t('product')"
            color="black"
            readonly/>
            <ProductAutocomplete
            v-else
            ref="product"
            :limitToContractProducts="false"
            :propFetchProducts="false"
            :productId="productId"
            :excludedProducts="excludedProductNames"
            :filteredProductIds="allowedProductIds"
            :required="true"
            :clearable="false"
            disableScalable
            hideRefresh
            @product-chosen="onProductChosen"/>
          </v-col>
          <v-col :cols="rateIsDerived ? 4 : 3">
            <v-text-field
            ref="volume"
            v-model.number="harvestVolume"
            :label="$t('volume')"
            :rules="[rules.required, rules.positiveDecimal, rules.twoDecimalPlacesOrFewer, rules.decimalBetweenOrBlankWithMessage(0, MAX_BID_HARVEST_VOLUME)]"
            type="number"
            suffix="t"
            color="black"
            hide-spin-buttons/>
          </v-col>
          <v-col v-if="!rateIsDerived" cols="3">
            <MoneyTextField
            :initialValue="enteredDeliveredRate"
            :label="$t('deliveredRate')"
            :readonly="mixedCosts && costsLocked"
            @val-changed="enteredDeliveredRate = $event"/>
          </v-col>
        </v-row>

        <div v-if="usesCostTypes && productId" class="d-flex align-center mb-1">
          <span class="subtitle-1 black--text mr-1">{{ productName ? $t('costsForProduct', { product: productName }) : $t('costComponents') }}</span>
          <Icon
          v-if="!costsLocked && !costEntryOpen && availableCostTypes.length > 0"
          icon="mdi-plus"
          iconColor="success"
          :small="false"
          tabindex="0"
          :tooltipText="$t('addCostComponent')"
          @icon-clicked="openCostEntry"/>
        </div>
        <v-card v-if="usesCostTypes && productId" outlined class="mb-2">
          <v-card-text class="pt-2 px-3 pb-2">
            <BidCostComponentList
            :key="entryKey"
            class="black--text"
            :costComponents="costRows"
            :readonly="costsLocked"
            :editableRates="!costsLocked"
            hideTotal
            @rate-changed="updateCostRate"
            @delete="removeCostRow"/>

            <v-row v-if="costEntryOpen && !costsLocked" dense no-gutters align="center" :key="`cost-${costEntryKey}`">
              <v-col cols="7" class="pr-2" @keydown.enter.prevent>
                <v-autocomplete
                ref="bidCostType"
                :items="availableCostTypes"
                item-text="name"
                item-value="bidCostTypeId"
                color="black"
                item-color="primary"
                v-model="bidCostTypeId"
                id="bid-product-cost-type-autocomplete"/>
              </v-col>
              <v-col cols="3" @keydown.enter.prevent="commitCostEntry">
                <MoneyTextField
                ref="costRate"
                :initialValue="rate"
                :label="$t('rate')"
                @val-changed="rate = $event"/>
              </v-col>
              <v-col cols="2" class="text-right">
                <Icon
                icon="mdi-check"
                iconColor="success"
                tabindex="0"
                :disabled="!costEntryComplete"
                :tooltipText="$t('addCostComponent')"
                @icon-clicked="commitCostEntry"/>
                <Icon
                icon="mdi-close"
                iconColor="error"
                tabindex="0"
                :tooltipText="$t('cancel')"
                @icon-clicked="closeCostEntry"/>
              </v-col>
            </v-row>

            <v-row v-if="!mixedCosts || effectiveCostRows.length > 0" dense no-gutters class="black--text mt-2">
              <v-col :cols="costsLocked ? 8 : 7" class="font-weight-bold">{{ $t(mixedCosts ? 'costComponents' : 'deliveredRate') }}</v-col>
              <v-col cols="4" class="text-right font-weight-bold">
                <Icon
                v-if="storedRateDiverged"
                icon="mdi-alert"
                iconColor="warning"
                :tooltipText="$t('bidRateDivergedFromCosts')"/>
                {{ formatMoney(mixedCosts ? costComponentsTotal : deliveredRate) }}
              </v-col>
              <v-col v-if="!costsLocked" cols="1"/>
            </v-row>
          </v-card-text>
        </v-card>

        <template #left-action>
          <v-btn text @click="$emit('close')">{{ isEditing ? $t('cancel') : $t('done') }}</v-btn>
        </template>
      </FormWrapper>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import fieldRules from '@/utils/rules.js'
import { formatMoney, numberWithCommas } from '@/utils/NumericMutations'
import { BidProductStatus } from '@/utils/Enumerations'
import { costComponentTotal, rateDivergesFromCosts, MAX_BID_RATE, MAX_BID_HARVEST_VOLUME } from '@/model/Bid.js'

export default {
  name: 'BidProductDialog',

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    FormWrapper: () => import('@/components/core/FormWrapper.vue'),
    ProductAutocomplete: () => import('@/components/autocomplete/ProductAutocomplete.vue'),
    MoneyTextField: () => import('@/components/helper/MoneyTextField.vue'),
    BidCostComponentList: () => import('@/components/bid/BidCostComponentList.vue'),
    Icon: () => import('@/components/helper/Icon.vue')
  },

  props: {
    bidId: {
      type: Number,
      required: true
    },
    destinationAccountId: {
      type: Number,
      required: true
    },
    product: {
      type: Object,
      default: null
    },
    siblingProducts: {
      type: Array,
      default: () => []
    },
    defaultCostComponents: {
      type: Array,
      default: () => []
    },
    costsLocked: {
      type: Boolean,
      default: false
    },
    mixedCosts: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    rules: fieldRules.rules,
    MAX_BID_HARVEST_VOLUME,
    productId: undefined,
    harvestVolume: undefined,
    enteredDeliveredRate: undefined,
    costRows: [],
    bidCostTypeId: undefined,
    rate: undefined,
    costEntryOpen: false,
    autoCostTypeId: undefined,
    costEntryKey: 0,
    allowedProductIds: [],
    addedProducts: [],
    selectedProductName: undefined,
    entryKey: 0,
    saving: false
  }),

  computed: {
    ...mapGetters('tract', ['allBidCostTypes']),
    ...mapGetters('product', { storeProducts: 'allProducts' }),

    isEditing () {
      return !!this.product?.bidProductId
    },

    usesCostTypes () {
      return this.allBidCostTypes.length > 0
    },

    rateIsDerived () {
      return this.usesCostTypes && !this.mixedCosts
    },

    productName () {
      return this.storeProducts.find(p => p.productId === this.productId)?.name
    },

    destinationProducts () {
      return this.siblingProducts.filter(p => p.bidProductId !== this.product?.bidProductId)
    },

    effectiveCostRows () {
      if (!this.costEntryComplete) return this.costRows
      const row = {
        bidCostTypeId: this.bidCostTypeId,
        bidCostTypeName: this.allBidCostTypes.find(bct => bct.bidCostTypeId === this.bidCostTypeId)?.name,
        rate: this.rate
      }
      return [...this.costRows, row]
    },

    costComponentsTotal () {
      return costComponentTotal(this.effectiveCostRows)
    },

    deliveredRate () {
      return this.rateIsDerived ? this.costComponentsTotal : this.enteredDeliveredRate
    },

    storedRateDiverged () {
      return this.isEditing && this.rateIsDerived && rateDivergesFromCosts(this.product)
    },

    complete () {
      return !!this.productId &&
        typeof this.harvestVolume === 'number' && this.harvestVolume > 0 && this.harvestVolume <= MAX_BID_HARVEST_VOLUME &&
        this.rules.twoDecimalPlacesOrFewer(this.harvestVolume) === true &&
        (this.rateIsDerived ? this.costsComplete : this.deliveredRate > 0 && this.costsValid) &&
        this.deliveredRate <= MAX_BID_RATE &&
        this.costComponentsTotal <= MAX_BID_RATE
    },

    costsComplete () {
      return this.costsValid && this.effectiveCostRows.length > 0
    },

    costsValid () {
      return (!this.costEntryPending || this.costEntryComplete) &&
        this.effectiveCostRows.every(r => r.rate > 0)
    },

    costEntryPending () {
      return typeof this.bidCostTypeId === 'number' && this.bidCostTypeId !== this.autoCostTypeId
    },

    costEntryComplete () {
      return typeof this.bidCostTypeId === 'number' && typeof this.rate === 'number' && this.rate > 0
    },

    availableCostTypes () {
      return this.allBidCostTypes.filter(bct => !this.costRows.some(r => r.bidCostTypeId === bct.bidCostTypeId))
    },

    excludedProductNames () {
      return [...this.destinationProducts, ...this.addedProducts]
        .filter(p => p.productId !== this.productId)
        .map(p => p.productName)
        .filter(Boolean)
    }
  },

  async created () {
    if (this.product) {
      this.productId = this.product.productId
      this.harvestVolume = this.product.harvestVolume
      this.enteredDeliveredRate = this.product.deliveredRate
      this.seedCostRows(this.product.costComponents)
    } else {
      this.seedCostRows(this.defaultCostComponents)
    }
    await this.loadAllowedProducts()
    if (!this.isEditing) {
      const remaining = this.allowedProductIds.filter(id => !this.destinationProducts.some(p => p.productId === id))
      if (remaining.length === 1) this.resetProductFields(remaining[0])
    }
  },

  mounted () {
    this.focusNextField()
  },

  methods: {
    ...mapActions('bid', ['createBidProduct', 'updateBidProduct']),
    ...mapActions('account', ['fetchAccountById']),

    formatMoney,
    numberWithCommas,

    async loadAllowedProducts () {
      const account = await this.fetchAccountById(this.destinationAccountId)
      this.allowedProductIds = (account.accountProducts ?? [])
        .filter(ap => !ap.isScalable)
        .map(ap => ap.productId)
    },

    onProductChosen (chosen) {
      this.productId = chosen ? chosen.productId : undefined
      this.selectedProductName = chosen ? chosen.name : undefined
    },

    focusNextField () {
      this.$nextTick(() => window.requestAnimationFrame(() => {
        if (this.$refs.product?.focus) this.$refs.product.focus()
      }))
    },

    async save () {
      if (!this.complete || this.saving) return
      this.saving = true
      try {
        const payload = {
          productId: this.productId,
          harvestVolume: this.harvestVolume,
          status: this.isEditing ? this.product.status : BidProductStatus.None.value,
          deliveredRate: this.deliveredRate,
          costComponents: this.effectiveCostRows.map(r => ({ bidCostTypeId: r.bidCostTypeId, rate: r.rate }))
        }
        if (this.isEditing) {
          await this.updateBidProduct({ bidId: this.bidId, bidProductId: this.product.bidProductId, payload })
          this.$emit('saved')
          this.$emit('close')
          return
        }
        await this.createBidProduct({ bidId: this.bidId, payload })
        this.addedProducts.push({ productId: this.productId, productName: this.selectedProductName })
        this.$emit('saved')
        const remainingProductIds = this.allowedProductIds.filter(id =>
          !this.addedProducts.some(p => p.productId === id) &&
          !this.destinationProducts.some(p => p.productId === id))
        if (remainingProductIds.length === 0) {
          this.$emit('close')
          return
        }
        this.resetProductFields(remainingProductIds.length === 1 ? remainingProductIds[0] : undefined)
      } finally {
        this.saving = false
      }
    },

    resetProductFields (nextProductId = undefined) {
      this.productId = nextProductId
      this.harvestVolume = undefined
      this.enteredDeliveredRate = undefined
      this.seedCostRows(this.defaultCostComponents)
      this.entryKey++
      if (nextProductId) this.focusVolume()
      else this.focusNextField()
    },

    focusVolume () {
      this.$nextTick(() => window.requestAnimationFrame(() => {
        if (this.$refs.volume) this.$refs.volume.focus()
      }))
    },

    seedCostRows (components) {
      this.costRows = components.map(c => ({ bidCostTypeId: c.bidCostTypeId, bidCostTypeName: c.bidCostTypeName, rate: c.rate }))
      this.resetCostEntry()
      this.costEntryOpen = this.costRows.length === 0
    },

    updateCostRate (row, rate) {
      this.costRows = this.costRows.map(r => r.bidCostTypeId === row.bidCostTypeId ? { ...r, rate } : r)
    },

    openCostEntry () {
      this.resetCostEntry()
      this.costEntryOpen = true
      this.focusCostEntry()
    },

    closeCostEntry () {
      this.resetCostEntry()
      this.costEntryOpen = false
    },

    commitCostEntry () {
      if (!this.costEntryComplete) return
      this.costRows = this.effectiveCostRows
      this.closeCostEntry()
    },

    removeCostRow (row) {
      const resetEntry = !this.costEntryPending && !(this.rate > 0)
      this.costRows = this.costRows.filter(r => r.bidCostTypeId !== row.bidCostTypeId)
      if (resetEntry) this.resetCostEntry()
      if (this.costRows.length === 0) this.costEntryOpen = true
    },

    resetCostEntry () {
      this.autoCostTypeId = this.availableCostTypes.length === 1 ? this.availableCostTypes[0].bidCostTypeId : undefined
      this.bidCostTypeId = this.autoCostTypeId
      this.rate = undefined
      this.costEntryKey++
    },

    focusCostEntry () {
      this.$nextTick(() => window.requestAnimationFrame(() => {
        const autoSelected = this.autoCostTypeId !== undefined && this.bidCostTypeId === this.autoCostTypeId
        const field = autoSelected ? this.$refs.costRate?.$children[0] : this.$refs.bidCostType
        if (field) field.focus()
      }))
    }
  }
}
</script>
